import { ReactElement } from "react"
import { useTranslation } from "react-i18next"

import IconDocument from "assets/img/icon-document.svg?react"

import Highlighter from "components/Highlighter/Highlighter"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import Lmid from "components/Lmid/Lmid"
import AssetArticle from "models/AssetArticle"
import Badge from "components/Badge/Badge"
import { Flex, FlexItem } from "components/Flex"
import Text from "components/Text/Text"
import InfoButton from "components/InfoButton/InfoButton"
import Tooltip from "components/Tooltip/Tooltip"

interface ISelectSearchOptionAsset {
    assetArticle: AssetArticle
    onShowDetailsClick(): void
    onFolderClick?(): void
    highlight?: string
}

const SelectSearchOptionAsset = ({
    assetArticle,
    onShowDetailsClick,
    onFolderClick,
    highlight = ""
}: ISelectSearchOptionAsset) => {
    const { t } = useTranslation()

    const renderBadges = () => {
        const badges: ReactElement[] = []

        const shouldShowVersionBadge = assetArticle.hasVersionInfo &&
            assetArticle.version &&
            (assetArticle.version > 1 || !assetArticle.isCurrentVersion)

        if (shouldShowVersionBadge) {
            const badgeTheme = assetArticle.isInactive ? "grey" : "green"
            badges.push(
                <Badge key="version" theme={badgeTheme}>
                    v{assetArticle.version}
                </Badge>
            )
        }

        if (badges.length === 0) return undefined
        return <Flex gap="0.4rem">{badges}</Flex>
    }

    const filterLabel = t('article.filterForArticle', { articleNo: assetArticle.articleNo })

    const tooltipContent = (
        <div>
            <div>{filterLabel}</div>
            <div>{assetArticle.articleDescription || t('article.noArticleDescription')}</div>
        </div>
    )

    return (
        <Flex
            className="w-100"
            justifyContent="space-between"
            alignItems="center">
            <FlexItem>
                <Text type="p">
                    <div className="d-inline-block valign-middle">
                        <ArticleDescription articleDescription={assetArticle.altArticleDescription || assetArticle.articleDescription} highlight={highlight} />
                    </div>
                    {' '}({t('common.lmid')}: <div className="d-inline-block valign-middle">
                        <Lmid
                            lmid={assetArticle.lmid}
                            labeled={assetArticle.isLabeled}
                            inCollection={assetArticle.linkedToAssetCollection}
                            highlight={highlight}
                            assetVersion={assetArticle.version}
                            assetVersioned={assetArticle.hasVersionInfo}
                            isCurrentVersion={assetArticle.isCurrentVersion}
                        />
                    </div>)
                </Text>
                <Text type="p" className="word-break-break-word">
                    {t('asset.yearOfConstruction')}: <Highlighter text={assetArticle.constructionYear.toString()} highlight={highlight} />
                    {typeof assetArticle.operationHours !== "undefined" && assetArticle.operationHours !== null &&
                        <>
                            <span className="px-1">|</span>
                            {t('asset.operatingHours')}: <Highlighter text={assetArticle.operationHours.toString()} highlight={highlight} /> {t('common.hoursShort')}
                        </>
                    }
                </Text>
            </FlexItem>
            <FlexItem shrink={0} className="ml-2">
                <Flex alignItems="center" gap="0.8rem">
                    {renderBadges()}
                    <Flex flow="column" alignItems="center" gap="0.4rem">
                        <InfoButton onClick={onShowDetailsClick} />
                        {onFolderClick && (
                            <Tooltip
                                trigger="hover"
                                delayShow={500}
                                content={tooltipContent}
                                placement="top">
                                <button
                                    className="select-search-option-asset__folder-btn"
                                    onClick={e => { e.stopPropagation(); onFolderClick() }}
                                    aria-label={filterLabel}
                                    type="button">
                                    <IconDocument />
                                </button>
                            </Tooltip>
                        )}
                    </Flex>
                </Flex>
            </FlexItem>
        </Flex>
    )
}

export default SelectSearchOptionAsset
